import { EditableImage } from '../content/EditableImage';
import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, AlertCircle, Heart } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const SpecialtyDetail = () => {
  const { specialtyId } = useParams();
  const { content } = useCmsContent();
  const { specialties } = content;
  const specialty = specialties.find(s => s.id === specialtyId);
  const specialtyIndex = specialties.findIndex(s => s.id === specialtyId);

  if (!specialty) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4"><EditableText contentKey="pages.specialtyDetail.notFound" /></h1>
          <Link to="/" className="text-brand-700 hover:text-brand-800">
            <EditableText contentKey="pages.specialtyDetail.returnHome" multiline={false} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 bg-gray-900">
        <EditableImage
          contentKey={`specialties.${specialtyIndex}.image`}
          alt={specialty.title}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-900/70 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-light mb-4"><EditableText contentKey={`specialties.${specialtyIndex}.title`} /></h1>
            <p className="text-xl"><EditableText contentKey="pages.specialtyDetail.heroSubtitle" /></p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-light text-gray-900 mb-6"><EditableText contentKey="pages.specialtyDetail.understanding" multiline={false} /> <EditableText contentKey={`specialties.${specialtyIndex}.title`} /></h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-12">
              <EditableText contentKey={`specialties.${specialtyIndex}.description`} />
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Symptoms */}
              <div className="bg-red-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <AlertCircle className="h-6 w-6 text-red-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900"><EditableText contentKey="pages.specialtyDetail.commonSymptoms" /></h3>
                </div>
                <ul className="space-y-2">
                  {specialty.symptoms.map((symptom, index) => (
                    <li key={index} className="text-gray-700 flex items-start">
                      <span className="mr-2">•</span>
                      <EditableText contentKey={`specialties.${specialtyIndex}.symptoms.${index}`} />
                    </li>
                  ))}
                </ul>
              </div>

              {/* Treatments */}
              <div className="bg-green-50 p-6 rounded-lg">
                <div className="flex items-center mb-4">
                  <Heart className="h-6 w-6 text-green-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900"><EditableText contentKey="pages.specialtyDetail.treatmentApproaches" /></h3>
                </div>
                <ul className="space-y-2">
                  {specialty.treatments.map((treatment, index) => (
                    <li key={index} className="text-gray-700 flex items-start">
                      <span className="mr-2">•</span>
                      <EditableText contentKey={`specialties.${specialtyIndex}.treatments.${index}`} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-brand-50 p-8 rounded-lg">
              <h3 className="text-2xl font-light text-gray-900 mb-4"><EditableText contentKey="pages.specialtyDetail.professionalHelp" /></h3>
              <p className="text-gray-700 mb-6">
                <EditableText contentKey="pages.specialtyDetail.helpPrefix" />{' '}
                <EditableText contentKey={`specialties.${specialtyIndex}.title`} />,{ ' '}
                <EditableText contentKey="pages.specialtyDetail.helpSuffix" />
              </p>
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-3 bg-brand-700 text-white rounded-md hover:bg-brand-800 transition-colors text-lg font-medium"
              >
                <EditableText contentKey="pages.specialtyDetail.consultation" multiline={false} /> <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Specialties */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-12">
            <EditableText contentKey="pages.specialtyDetail.otherAreas" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {specialties
              .filter(s => s.id !== specialtyId)
              .slice(0, 3)
              .map((otherSpecialty) => {
                const otherIndex = specialties.findIndex((candidate) => candidate.id === otherSpecialty.id);
                return (
                <Link
                  key={otherSpecialty.id}
                  to={`/specialty/${otherSpecialty.id}`}
                  className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <EditableImage
                    contentKey={`specialties.${otherIndex}.image`}
                    alt={otherSpecialty.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      <EditableText contentKey={`specialties.${otherIndex}.title`} />
                    </h3>
                    <span className="text-brand-700 text-sm font-medium"><EditableText contentKey="pages.specialtyDetail.learnMore" multiline={false} /></span>
                  </div>
                </Link>
                );
              })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SpecialtyDetail;
